import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import styled from 'styled-components';
import { media, spacing } from "../../theme";
import {ReactComponent as PhotoIcon} from '/public/assets/css/ontario-design-system/icons/svg/ontario-icon-photo.svg';
import {ReactComponent as UploadIcon} from '../../../public/assets/css/ontario-design-system/icons/svg/ontario-icon-upload.svg';
import {ReactComponent as CloseIcon} from '../../../public/assets/css/ontario-design-system/icons/ontario-icon-close.svg';
import { VscPreview } from "react-icons/vsc";
import { SlNote } from "react-icons/sl";
import colors from "../../theme/variables/colors";
import { InputText } from "primereact/inputtext";
import { FormSectionElements } from "../../entities/formTemplateDataType";
import { fileTypeList, UploadFileSizeLimit } from "../../entities/Constants";
import ErrorElement from "../../commonComponent/ErrorElement";
import { ModalComponent } from "./ModalComponent";
import { PdfPreviewComponent, DocViewerComponent, ImageViewerComponent, MsDocViewer } from "./FilePreviewComponent";
import { LiveValidationErrMsg } from "../live-validation-errMsg/LiveValidationErrMsg";

const DocumentUploadContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing.spacing7};
    margin-top: ${spacing.spacing7};

    @media ${media.medium} {
        flex-direction: row;
    }
`;

const FileNameContainer = styled.div<{ $background?: string; $minWidth?: string; $maxWidth?: string; }>`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    background: ${props => props.$background || '#E2F0F4'};
    box-shadow: ${props => props.$background ? '' : '0px 1px 2px rgba(0, 0, 0, 0.25)'};
    padding: ${props => props.$background ? `${spacing.spacing2} ${spacing.spacing0}` : `${spacing.spacing2} ${spacing.spacing2} ${spacing.spacing2} ${spacing.spacing4}`};
    min-width: ${props => props.$minWidth || '300px'};
    gap: ${spacing.spacing4};
    max-width: ${props => props.$maxWidth || '400px'};
    width: 100%;
    font-size: 0.9rem;

    // @media ${media.medium} {
    //     gap: ${spacing.spacing5};
    // }

    .close-icon {
        cursor: pointer;
        flex-shrink: 0;
        width: 24px;
        height: 24px;
    }
    
    &:hover {
        background: ${props => props.$background ? '#ffffff;' : '#C2E0FF;' };
    }
`;

const FileNameDiv = styled.div`
    display: flex;
    align-items: flex-start;
    word-break: break-word;
    flex-wrap: wrap;
    gap: ${spacing.spacing1};
    
    
    // @media ${media.medium} {
    //     gap: ${spacing.spacing1};
    // };

    svg: {
        width: 24px;
        height: 24px;
        path {
            fill: {colors.btnPrimaryBG};
        }
    }
`;

export const StyledPhotoIcon = styled(PhotoIcon)`
    flex-shrink: 0;
    path {
        fill: ${colors.btnPrimaryBG};
    }

    width: 24px;
    height: 24px;
`;

const UploadButton = styled.button`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: ${spacing.spacing4};
    border: 1px dashed ${colors.palette.black};
    border-radius: 3px;
    background-color: ${colors.palette.greyscale5};
    padding: ${spacing.spacing4};
    cursor: pointer;
`;

export type UPloadFileItemProps = {
    file: File;
    attachment?: string;
    attachmentFRA?: string;
    path?: string;
};

export type FileUploadProps = {
    field: FormSectionElements;
    lang: 'en' | 'fr';
    title?: string;
    setFieldValue: (value: any) => void;
}

const FileUploadComponent = ({
    field,
    title, 
    lang,
    setFieldValue,
}: FileUploadProps) => {
    const [files, setFiles] = useState<UPloadFileItemProps[]>([]);
    const uploadRef = useRef(null);
    const [errors, setErrors] = useState<string[]>([]);
    //const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    //const [desc, setDesc] = useState('');
    const [current, setCurrent] = useState<UPloadFileItemProps|null>(null);
    const [modalType, setModalType] = useState<'Preview'|'Description'|null>(null);

    const KB = 1024;
    const sizeLimit = (field.FileUpload?.FileSizeLimit || UploadFileSizeLimit.size) * (field.FileUpload?.SizeUnite === 'GB' ? KB * KB * KB : KB * KB);
    const unite = ['B', 'KB', 'MB', 'GB'];

    // useEffect(() => {
    //     setFieldValue(files);
    //     setErrors([]);
    // }, [files]);

    useEffect(() => {
        const initFiles = field.Value ?? [] as UPloadFileItemProps[];
        setFiles(initFiles);
        setErrors([]);
        setCurrent(null);
        setModalOpen(false);
    }, [field.Value]);

    useEffect(() => {
        console.log(field.ErrorMessage);
    }, [field.ErrorMessage, field.ErrorMessageFRA]);

    const fileList = field.FileUpload?.AllowedForUploadFileTypeList
        .map(type => type.label)
        .join(', ');

    const getFileType = (file?: File) => {
        return fileTypeList.filter(type => type.val.some(val => val.mimeType === file?.type))?.[0]?.label ?? '';
    }

    const getFileSize = (file: File) => {
        let size = file.size, index = 0;
        while(size > KB) {
            size = size/KB;
            if (++index === unite.length-1) {
                break;
            }
        }
        return size.toFixed(2) + unite[index];
    }

    const handleRemoveFile = (fileName: string, path: string) => {
        const newFiles = files.filter(file => file.file.name !== fileName || file.path !== path);
        if (uploadRef.current.value === path) {
            uploadRef.current.value = '';
        }
        setFieldValue(newFiles);
    };

    const isFileValid = (file: File, path: string) => {
        let inValid = [];
        if (files?.filter(f => f.path === path && file.name === f.file.name).length) {
            return false;
        }

        if (file.size > sizeLimit) {
            inValid.push(`${lang==='en'? 'File size is execced the limit' : 'La taille du fichier dépasse la limite'}`);
        }
    
        const support = field.FileUpload?.AllowedForUploadFileTypeList?.map(p=>p?.val)
            .filter(Boolean)
            .flat()
            .some(p => p && file.type===p.mimeType);
            
        if (!support) {
            inValid.push(`${lang==='en'? 'File type is not supported' : "Le type de fichier n'est pas pris en charge"}`);
        }
        
        setErrors(inValid);
        return !inValid.length;
    }
    const convertArrayBufferToString = (input: string | ArrayBuffer) => {
        if (typeof input == 'string') {
            return input;
        } else {
            const textDecoder = new TextDecoder('utf-8'); 
            const text = textDecoder.decode(input);
            return text;
        }
    }
    const handleFileUploadChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files[0];
        const path = e.target.value;
     
        if (file && isFileValid(file, path)) {
            const newFiles = [...files];
            const attachment=await readFileContent(file);
            newFiles.push({file, path,attachment});
            setFieldValue(newFiles);
        }
    }

    const readFileContent = async (file) => {
        let content;
        var reader = new FileReader();

        const readFile = () => {
            return new Promise<void>((resolve, reject) => {
                reader.readAsDataURL(file);
                reader.onload = function (e) {
                    content = reader.result;
                    resolve();
                }

                reader.onerror = function (e) {
                    reject(e);
                };
            })
        }

        await readFile();

        return content;
    }

    const handleAttachmentChange = (e: ChangeEvent<HTMLInputElement>, findex: number) => {
        const newFiles = files.map((file, index) => (index === findex ? lang==='en'? {...file, attachment: e.target.value} : {...file, attachmentFRA: e.target.value} : file));
        setFieldValue(newFiles);
    }

    const handlePreviewFile = (file: UPloadFileItemProps) => {
        setModalType('Preview');
        setCurrent(file);
        setModalOpen(true);
    }

    const handleCloseModal = () => {
        setCurrent(null);
        setModalType(null);
        setModalOpen(false);
    }

    const handleDescription = (file: UPloadFileItemProps) => {
        //console.log(`file description: ${file.attachment}`);
        setCurrent(file);
        setModalType('Description');
        setModalOpen(true);
    }

    const handleUpdateDescription = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const newFiles = files.map(file => (file.file.name === current.file.name ? lang==='en' ? {...file, attatchment: current.attachment} : {...file, attatchmentFRA: current.attachment} : file));
        setFieldValue(newFiles);
        handleCloseModal();
    }

    return ( 
        <>
            <ModalComponent modalIsOpen={modalOpen} 
                shouldCloseOnOverlayClick 
                onRequestClose={handleCloseModal} 
                fileViewer={['pdf', 'word', 'excel'].includes(getFileType(current?.file).toLowerCase())}
            >
                {modalType === 'Preview' && (
                    <>
                        {['pdf', 'png', 'gif'].includes(getFileType(current.file).toLowerCase()) && (
                            <ImageViewerComponent filename={current.file.name} imageData={current.attachment} imageType={getFileType(current.file)}/>
                        )}
                        {getFileType(current.file).toLowerCase()==='word' && (
                            <MsDocViewer docData={current.attachment} fileName={current.file.name} />
                        )}
                    </>
                )}
                {modalType === 'Description' &&  (
                    <>
                        <p>
                            <label htmlFor={`file-description-${field.Id}`}>
                                <strong>{(lang==='en' ? 'File description for ' : 'Description du fichier pour ') + current?.file.name}</strong>{lang==='en'? ' (optional)': ' (facultatif)'}
                            </label>
                        </p>
                        <InputText 
                            id={`attachement-${field.Id}`}
                            value={lang==='en' ? current.attachment : current.attachmentFRA}
                            type="text"
                            maxLength={250}
                            onChange={(e) => setCurrent(lang==='en' ? {...current, attachment: e.target.value}: {...current, attachmentFRA: e.target.value})}
                            style={{width: '100%', marginBottom: '-8px', border: '1px solid #0066cc'}}
                        />

                        <div className="ontario-margin-top-32-!" style={{display: 'flex'}}>
                            <button className="ontario-button ontario-button--primary" onClick={handleUpdateDescription}>Ok</button> 
                            <button className="ontario-button ontario-button--secondary" onClick={handleCloseModal}>{lang==='en'? 'Cancel' : 'Annuler'}</button>
                        </div>
                    </>
                )}
            </ModalComponent>
            
            <LiveValidationErrMsg
                field={field}
                isEng={lang==='en'}
                key={"LiveValidationErrMsg_" + field.LoadBindName}
            />

            <DocumentUploadContainer>
                <div style={{display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '500px'}}>
                    {title && <h2>{title}</h2>}
                    
                    {errors.length>0 && (
                        <div className="ontario-margin-top-16-!">
                            <ErrorElement message={errors.join('; ')} display />
                        </div>
                    )} 
                    <UploadButton
                        type="button"
                        onClick={() => uploadRef.current.click()}
                    >
                        <div style={{display: 'flex'}}>
                            <div style={{marginRight: '0.5rem'}}>
                                <UploadIcon fill='#0066CC' style={{width: '24px', height: '24px' }}/>
                            </div>
                            <div>
                                <p style={{textAlign: 'left', fontSize: '20px', fontWeight: '700', marginBottom: '0.5rem', color: '#0066CC'}}>
                                    <strong>{lang==='en'? 'Upload a file': 'Télécharger un fichier'}</strong>
                                </p>
                                <p style={{textAlign: 'left', fontSize: '17', marginBottom: '0.5rem'}}>
                                    {fileList + (lang==='en'? ', max size ' : ', taille maximale ') + (field.FileUpload?.FileSizeLimit || UploadFileSizeLimit.size) + (field.FileUpload?.SizeUnite || UploadFileSizeLimit.unite)}
                                </p>
                            </div>
                        </div>
                    </UploadButton>
                    <InputText style={{display: 'none'}} id={`upload-file-${field.Id}`} type="file" ref={uploadRef} onChange={(e) =>handleFileUploadChange(e)}/>
                </div>
            </DocumentUploadContainer>

            {files.length>0 && (
                    <div className="ontario-margin-top-32-! ontario-margin-bottom-16-!">
                        <h3>{lang==='en'? 'Uploaded files': 'Fichiers téléchargés'}</h3>
                        <div className="ontario-margin-top-16-!">
                            {files.map((file, index) => (
                                <div className="upload-item">
                                    <FileNameContainer $maxWidth={!field.FileUpload.LegacyStyle ? '500px' : ''}>
                                        <FileNameDiv>
                                            <StyledPhotoIcon
                                                className="ontario-icon"
                                                preserveAspectRatio="xMidYMid meet"
                                            />
                                            <span style={{textAlign: 'left'}}>{file.file.name}</span>
                                        </FileNameDiv>
                                        <FileNameDiv>
                                            <span>{getFileSize(file.file)}</span>
                                            {field.FileUpload?.PreviewFile && !field.FileUpload.LegacyStyle && (
                                                <div role='image' aria-label="Prewiew" title={lang==='en'? 'Preview' : 'Aperçu'}>
                                                    <VscPreview style={{fill:"#0066cc", height: '24', width:'24'}} onClick={() => handlePreviewFile(file)}/>
                                                </div>
                                            )}
                                            {field.FileUpload?.hasDescription && !field.FileUpload.LegacyStyle && (
                                                <div role='image' aria-label="Description" title={lang==='en'? (file.attachment || 'Description') : (file.attachment || 'Description')}>
                                                    <SlNote style={{fill:"#0066cc", height: '20', width:'20', marginTop: '1px'}} onClick={() => handleDescription(file)}/>
                                                </div>
                                            )}
                                            <div role='image' aria-label="Delete" title={lang==='en'? 'Delete' : 'Supprimer'}>
                                                <CloseIcon style={{fill:"#0066cc", height: '24', width:'24'}} onClick={() => handleRemoveFile(file.file.name, file.path)} />
                                            </div>
                                        </FileNameDiv>
                                    </FileNameContainer>
                                    <p hidden>{file.attachment}</p>
                                    {field.FileUpload?.LegacyStyle && field.FileUpload?.hasDescription && (
                                        <FileNameContainer $background="#ffffff" $minWidth="300px">
                                            <div style={{display: 'block', width: '100%'}}>
                                                <label htmlFor={`attachement-${field.Id}-${index}`}>
                                                    <strong>{lang==='en'? 'File description' : 'Description du fichier'}</strong>{lang==='en'? ' (optional)': ' (facultatif)'}
                                                </label>
                                                <InputText 
                                                    id={`attachement-${field.Id}-${index}`}
                                                    value={lang==='en'? file.attachment : file.attachmentFRA}
                                                    type="text"
                                                    maxLength={250}
                                                    onChange={(e) => handleAttachmentChange(e, index)}
                                                    style={{width: '100%', marginBottom: '-8px', border: '1px solid #0066cc'}}
                                                />
                                            </div>
                                        </FileNameContainer>
                                    )}
                                    {field.FileUpload?.LegacyStyle && field.FileUpload?.PreviewFile && (
                                        <FileNameContainer $background="#ffffff" $minWidth="5rem" $maxWidth="8rem">
                                            <button className="ontario-button ontario-button--secondary" 
                                                style={{marginBottom: '-6px', lineHeight: '1.3', color: '#0066cc', borderRadius: '4px', padding: '0.5rem 2rem' }} 
                                                onClick={() => handlePreviewFile(file)}
                                            >Preview</button>
                                        </FileNameContainer>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
        </>
    )
};

export default FileUploadComponent;
